/**
 * DS Coder — tiny dependency-free glob matcher for rule targeting.
 *
 * Supports the common subset used by editor rule globs:
 *   **      → matches across path segments (any depth, incl. none)
 *   *       → matches within a single segment (no "/")
 *   ?       → matches a single non-"/" character
 *   {a,b}   → alternation
 * Patterns are matched against POSIX-style repo paths (e.g. "src/app/page.tsx").
 * A rule's `globs` field may hold several patterns separated by commas or newlines.
 */

function globToRegExp(glob: string): RegExp {
  let re = "";
  for (let i = 0; i < glob.length; i++) {
    const c = glob[i];
    switch (c) {
      case "*":
        if (glob[i + 1] === "*") {
          // "**" (optionally followed by "/") → any depth
          i++;
          if (glob[i + 1] === "/") i++;
          re += "(?:.*/)?";
        } else {
          re += "[^/]*";
        }
        break;
      case "?":
        re += "[^/]";
        break;
      case "{":
        re += "(?:";
        break;
      case "}":
        re += ")";
        break;
      case ",":
        re += "|";
        break;
      // Escape regex metacharacters.
      case ".":
      case "+":
      case "^":
      case "$":
      case "(":
      case ")":
      case "|":
      case "[":
      case "]":
      case "\\":
        re += `\\${c}`;
        break;
      default:
        re += c;
    }
  }
  return new RegExp(`^${re}$`);
}

/** Split a rule's `globs` field into individual, trimmed patterns. */
export function parseGlobs(globs: string): string[] {
  return globs
    .split(/[\n,]/)
    .map((g) => g.trim())
    .filter(Boolean);
}

/**
 * True if any of the given glob patterns matches any of the paths.
 * A bare filename pattern (no "/") also matches the file's basename in any
 * directory, e.g. "*.ts" matches "src/lib/x.ts".
 */
export function anyGlobMatches(patterns: string[], paths: string[]): boolean {
  if (patterns.length === 0 || paths.length === 0) return false;

  const regexps = patterns.map((p) => {
    const full = globToRegExp(p);
    // If the pattern has no path separator, also match against basenames.
    const basename = p.includes("/") ? null : globToRegExp(p);
    return { full, basename };
  });

  for (const path of paths) {
    const base = path.includes("/") ? path.slice(path.lastIndexOf("/") + 1) : path;
    for (const { full, basename } of regexps) {
      if (full.test(path)) return true;
      if (basename && basename.test(base)) return true;
    }
  }
  return false;
}
